import * as THREE from 'three';
export type Viewport = {
  left: number;
  top: number;
  width: number;
  height: number;
  screenWidth: number;
  screenHeight: number;
};
type Point = { x: number; y: number };
const area = (points: Point[]) =>
  Math.abs(
    points.reduce((sum, point, i) => {
      const next = points[(i + 1) % points.length]!;
      return sum + point.x * next.y - next.x * point.y;
    }, 0),
  ) / 2;
function clippedFraction(
  points: Point[],
  bounds: { left: number; right: number; bottom: number; top: number },
) {
  const original = area(points);
  if (!original || bounds.left >= bounds.right || bounds.bottom >= bounds.top) return 0;
  let polygon = points;
  for (const [axis, value, direction] of [
    ['x', bounds.left, 1],
    ['x', bounds.right, -1],
    ['y', bounds.bottom, 1],
    ['y', bounds.top, -1],
  ] as const) {
    const input = polygon;
    polygon = [];
    for (let i = 0; i < input.length; i++) {
      const a = input[i]!,
        b = input[(i + 1) % input.length]!;
      const insideA = (a[axis] - value) * direction >= 0,
        insideB = (b[axis] - value) * direction >= 0;
      if (insideA) polygon.push(a);
      if (insideA !== insideB) {
        const t = (value - a[axis]) / (b[axis] - a[axis]);
        polygon.push({ x: a.x + t * (b.x - a.x), y: a.y + t * (b.y - a.y) });
      }
    }
  }
  return Math.min(1, area(polygon) / original);
}
export function elementIsPainted(element: HTMLElement) {
  for (let node: HTMLElement | null = element; node; node = node.parentElement) {
    const style = getComputedStyle(node);
    if (style.visibility === 'hidden' || style.display === 'none' || Number(style.opacity) <= 0) return false;
  }
  return true;
}
/** Uniform plane-area samples check camera clipping, actual scene occlusion and DOM coverage. */
export function billboardVisibility(
  face: THREE.Mesh,
  camera: THREE.Camera,
  occluders: THREE.Object3D[],
  viewport: Viewport,
  canvasExposed: (x: number, y: number) => boolean,
  ray = new THREE.Raycaster(),
) {
  if (!face.visible || viewport.width <= 0 || viewport.height <= 0) return 0;
  face.updateWorldMatrix(true, false);
  const geometry = face.geometry as THREE.PlaneGeometry;
  const { width, height } = geometry.parameters;
  if (!(width > 0 && height > 0)) return 0;
  const corners = [
    [-0.5, -0.5],
    [0.5, -0.5],
    [0.5, 0.5],
    [-0.5, 0.5],
  ].map(([x, y]) =>
    new THREE.Vector3(x! * width, y! * height, 0).applyMatrix4(face.matrixWorld).project(camera),
  );
  if (
    corners.every((p) => p.x < -1) ||
    corners.every((p) => p.x > 1) ||
    corners.every((p) => p.y < -1) ||
    corners.every((p) => p.y > 1) ||
    corners.every((p) => p.z < -1 || p.z > 1)
  )
    return 0;
  let visible = 0;
  for (const u of [-0.375, -0.125, 0.125, 0.375])
    for (const v of [-0.375, -0.125, 0.125, 0.375]) {
      const point = new THREE.Vector3(u * width, v * height, 0)
        .applyMatrix4(face.matrixWorld)
        .project(camera);
      if (point.x < -1 || point.x > 1 || point.y < -1 || point.y > 1 || point.z < -1 || point.z > 1) continue;
      const x = viewport.left + ((point.x + 1) * viewport.width) / 2;
      const y = viewport.top + ((1 - point.y) * viewport.height) / 2;
      if (x < 0 || y < 0 || x >= viewport.screenWidth || y >= viewport.screenHeight || !canvasExposed(x, y))
        continue;
      ray.setFromCamera(new THREE.Vector2(point.x, point.y), camera);
      const hits = ray.intersectObjects(occluders, true);
      if (hits[0]?.object === face) visible++;
    }
  return Math.min(
    visible / 16,
    clippedFraction(corners, {
      left: Math.max(-1, (-viewport.left / viewport.width) * 2 - 1),
      right: Math.min(1, ((viewport.screenWidth - viewport.left) / viewport.width) * 2 - 1),
      bottom: Math.max(-1, 1 - ((viewport.screenHeight - viewport.top) / viewport.height) * 2),
      top: Math.min(1, 1 + (viewport.top / viewport.height) * 2),
    }),
  );
}
export function domVisibility(element: HTMLElement) {
  const rect = element.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0 || !elementIsPainted(element)) return 0;
  let visible = 0;
  for (const u of [0.125, 0.375, 0.625, 0.875])
    for (const v of [0.125, 0.375, 0.625, 0.875]) {
      const x = rect.left + u * rect.width,
        y = rect.top + v * rect.height;
      if (x < 0 || y < 0 || x >= innerWidth || y >= innerHeight) continue;
      const hit = document.elementFromPoint(x, y);
      if (hit && element.contains(hit)) visible++;
    }
  const width = Math.max(0, Math.min(rect.right, innerWidth) - Math.max(rect.left, 0));
  const height = Math.max(0, Math.min(rect.bottom, innerHeight) - Math.max(rect.top, 0));
  return Math.min(visible / 16, (width * height) / (rect.width * rect.height));
}
